import { useContext, useCallback } from 'react';
import { LocaleContext, type SupportedLocale } from '../contexts/LocaleContext';
import en from '../locales/en.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';

type TranslationKey = string;

interface TranslationParams {
  [key: string]: string | number;
}

type TranslationArg = string | TranslationParams | undefined;

const translations = { en, fr, es };

const getNested = (obj: any, path: string[]): any => {
  return path.reduce((acc, k) => (acc ? acc[k] : undefined), obj);
};

export const useTranslation = () => {
  const { locale, setLocale } = useContext(LocaleContext);

  const currentTranslations = translations[locale as keyof typeof translations] || translations.fr;

  /**
   * Translation function with multiple overloads:
   * - t('key') - returns translation or key
   * - t('key', { params }) - returns translation with interpolated params
   * - t('key', 'fallback') - returns translation or fallback string
   * - t('key', 'fallback', { params }) - returns translation or fallback with params
   */
  const t = useCallback((key: TranslationKey, arg1?: TranslationArg, arg2?: TranslationParams): string => {
    const keys = key.split('.');

    // Parse arguments - support both (key, params) and (key, fallback, params)
    let fallbackText: string | undefined;
    let params: TranslationParams | undefined;

    if (typeof arg1 === 'string') {
      fallbackText = arg1;
      params = arg2;
    } else if (typeof arg1 === 'object') {
      params = arg1;
    }

    let value: any = getNested(currentTranslations, keys);

    // Fallback to French (primary language) then English if missing
    if (typeof value !== 'string') {
      const frFallback = getNested(translations.fr, keys);
      if (typeof frFallback === 'string') {
        value = frFallback;
      } else {
        const enFallback = getNested(translations.en, keys);
        if (typeof enFallback === 'string') {
          value = enFallback;
        }
      }
    }

    // If still no value, use provided fallback or key
    if (typeof value !== 'string') {
      if (fallbackText) {
        value = fallbackText;
      } else {
        return key;
      }
    }

    // Interpolate params
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match: string, paramKey: string) => {
        return params![paramKey]?.toString() || match;
      });
    }

    return value;
  }, [currentTranslations]);

  const updateLocale = useCallback((newLocale: string) => {
    setLocale(newLocale as SupportedLocale);
  }, [setLocale]);

  return { t, locale, setLocale: updateLocale };
};
